import { Form, Formik } from 'formik';

import { Grid } from '@/molecules';
import { CardProductWordpress } from '@/templates';

import { useAddToCart, useBundle, useBundleDispatch } from '@/lib/hooks';
import { ProductForm } from './ProductForm';
import { ProductFormMobile } from './ProductFormMobile';
import {
  generateBundleMetadata,
  Quantity,
  QuantitySchema,
  useProductForm,
} from './utils';

import type { TypeProductDescriptionProps } from '../product-description';
import type { ProductFormLayout, ProductFormValues } from './types';

interface TypeBundleCustomizableProductForm
  extends Pick<
      TypeProductDescriptionProps,
      'price' | 'regularPrice' | 'type' | 'externalUrl'
    >,
    ProductFormLayout {}

export const BundleCustomizableProductForm = ({
  productId,
  price,
  regularPrice,
  moreInfo,
  stock,
  type,
  externalUrl,
  stockAlert,
  isSellerClosed,
}: TypeBundleCustomizableProductForm) => {
  const bundle = useBundle();
  const dispatch = useBundleDispatch();
  const { state, add } = useAddToCart({ onAdd: dispatch && dispatch.reset });
  const {
    formRef: { mobile, desktop },
  } = useProductForm<ProductFormValues>({ productId });
  const formik = {
    onSubmit: (data: ProductFormValues) =>
      add({
        productId,
        quantity: data.cantidad,
        extraData: bundle ? generateBundleMetadata(bundle) : undefined,
        gtm: { listName: 'product' },
      }),
    initialValues: { cantidad: 1 },
    validationSchema: QuantitySchema,
    isInitialValid: true,
  };
  const productForm = {
    productId,
    isSellerClosed,
    price: bundle?.autoCalculatePrice ? bundle?.totalPrice : price,
    regularPrice: bundle?.autoCalculatePrice
      ? bundle?.totalRegularPrice
      : regularPrice,
    type,
    state,
    moreInfo,
    stock,
    stockAlert,
    externalUrl,
  };
  const disabled = Boolean(
    bundle &&
      bundle.limitWhole.min &&
      bundle.numProducts < bundle.limitWhole.min,
  );

  return (
    <>
      <Grid md={3} lg={3} className="mb-6">
        {bundle?.initialProducts.map((product, index) => (
          <Grid.Item key={product.id}>
            <CardProductWordpress
              product={product}
              numColumns={index}
              type={bundle?.autoCalculatePrice ? 'COMPLETE' : 'SIMPLE'}
              editable
              disableFreeShipping
            />
          </Grid.Item>
        ))}
      </Grid>

      <Formik innerRef={desktop} validateOnChange {...formik}>
        <Form className="relative hidden flex-col lg:flex" noValidate>
          <ProductForm
            quantitySelector={
              <Quantity theme="product" className="h-[46px] w-auto" />
            }
            disabled={disabled}
            {...productForm}
          />
        </Form>
      </Formik>

      <ProductFormMobile
        quantitySelector={
          <Quantity
            placeholder="Cantidad:"
            classNameSelect="!h-full !text-base"
            theme="tertiary"
            reverse
          />
        }
        {...productForm}
      >
        {(children) => (
          <Formik innerRef={mobile} {...formik}>
            <Form
              className="fixed bottom-0 left-0 right-0 z-20 grid h-14 grid-cols-[repeat(auto-fit,_minmax(0,_1fr))] lg:hidden"
              noValidate
            >
              {children({ disabled })}
            </Form>
          </Formik>
        )}
      </ProductFormMobile>
    </>
  );
};
